import {AddUserType} from "../@const";
import FieldProto from "../../../fields3/proto/field-proto";
import {MenuItem} from "../../../gd-components/menu/@const";
import Department from "../../../model2/department/department";
import {CompanyRole, CompanyRoleLabel} from "../../../gd/const/company-role";
import UserSkillsCollection from "../../../../app/settings/users/skills/collection/collection";
import UserSkill from "../../../../app/settings/users/skills/collection/model";
import Workspace from "../../../model2/space/workspaces/workspace";
import ProjectSession from "../../../model2/project/project-session";
import UsersToAdd from "./collection";
import {FieldType} from "../../../fields3/field-type";
import FieldWorkspaceAccess from "../../../fields3/system/project/access/workpace-access";
import FieldProjectAccess from "../../../fields3/system/project/access/project-access";
import Task from "../../../model2/task/task"
import AddUser_createUsers from "../actions/create-users"
import {ProjectAccess} from "../../../gd/const/project"

// const debug = "AddUser_Manager"
const debug = false

export const AddUser_ALL = 'all'

export type AddUser_Callback =  {(manager: AddUser_Manager): void}

export class AddUser_Manager {

    public type             : AddUserType
    public searchPrompt     : string = ''

    public groupByFieldId   : ID
    public groupByValue     : any

    public reRender        : callback
    public groupByFields  : {[key:string]: FieldProto} = {}

    public workspace?       : Workspace         // workspace we add to
    public project?         : ProjectSession    // project we add to
    public task?            : Task              // task we add to

    public workspaceUsersToAdd  : UsersToAdd = new UsersToAdd(this)
    public companyUsersToAdd    : UsersToAdd = new UsersToAdd(this)
    public projectUsersToAdd    : UsersToAdd = new UsersToAdd(this)

    public workspaceAccessField : FieldWorkspaceAccess
    public projectAccessField   : FieldProjectAccess

    public multiselect          : boolean = true
    public noUsersCreation      : boolean = false   // no requests will be made to create users in workspace, project or task

    public callback?        : AddUser_Callback      // called when all done and users added


    constructor(type:AddUserType, minProjectAccess?: ProjectAccess) {

        this.type               = type
        this.reRender           = () => { console.warn('Not implemented'); }

        // take first option
        this.groupByFieldId     = this.groupByOptions[0]!.value
        this.groupByValue       = AddUser_ALL

        this.groupByFields      = {
            'departmentId'          : gd.fields.companyUser.departmentId,
            'skills'                : gd.fields.companyUser.skills,
            'companyRole'           : gd.fields.companyUser.companyRole
        }

        this.workspaceAccessField = new FieldWorkspaceAccess({
            type            : FieldType.WORKSPACE_ACCESS,
            fieldId         : 'workspaceAccess'
        })

        this.projectAccessField = new FieldProjectAccess({
            type            : FieldType.PROJECT_ACCESS,
            fieldId         : 'projectAccess',
            minAccess       : minProjectAccess // minimum project access that can be selected (for example: for Task it can't be lower than 'Limited')
        })
    }

    static initForWorkspace(workspace:Workspace, callback?:callback):AddUser_Manager {
        const manager       = new AddUser_Manager(AddUserType.WORKSPACE_USER)

        manager.workspace   = workspace
        manager.callback    = callback

        manager.companyUsersToAdd.resetForWorkspace(workspace)

        return manager
    }

    static initForProject(project:ProjectSession, callback?:callback):AddUser_Manager {
        const manager       = new AddUser_Manager(AddUserType.PROJECT_USER)

        manager.project     = project
        manager.workspace   = project.workspace || undefined
        manager.callback    = callback


        if (manager.workspace) {
            manager.workspaceUsersToAdd.resetForProjectInWorkspace(manager.workspace, project)
            manager.companyUsersToAdd.resetForWorkspace(manager.workspace)
        } else {
            manager.companyUsersToAdd.resetForProjectWithoutWorkspace(project)
            console.error('Not implemented yet #9ed876323')
        }



        return manager
    }

    static initForTask(task:Task, callback?:callback):AddUser_Manager {
        const manager               = new AddUser_Manager(AddUserType.TASK_USER, ProjectAccess.LIMITED)

        const project               = task.project!

        manager.task                = task
        manager.project             = project
        manager.workspace           = project.workspace || undefined
        manager.callback            = callback

        if (manager.workspace) {
            manager.projectUsersToAdd.resetForTask(task)
            manager.workspaceUsersToAdd.resetForProjectInWorkspace(manager.workspace, project, task)
            manager.companyUsersToAdd.resetForWorkspace(manager.workspace, task)
        } else {
            manager.projectUsersToAdd.resetForTask(task)
            manager.companyUsersToAdd.resetForProjectWithoutWorkspace(project, task)
        }

        return manager
    }


    get groupByOptions():Option[] {

        const options : Option[] = [

            {
                value   : gd.fields.companyUser.departmentId.fieldId,
                label   : gd.fields.companyUser.departmentId.label,
                icon    : gd.fields.companyUser.departmentId.icon!,
            },

            {
                value   : gd.fields.companyUser.skills.fieldId,
                label   : gd.fields.companyUser.skills.label,
                icon    : gd.fields.companyUser.skills.icon!,
            },

            {
                value   : gd.fields.companyUser.companyRole.fieldId,
                label   : gd.fields.companyUser.companyRole.label,
                icon    : gd.fields.companyUser.companyRole.icon!,
            },
        ]

        return options



    }

    get searchEnabled (): boolean {
        return !!this.searchPrompt
    }

    public setSearchPrompt = (prompt:string):void => {
        this.searchPrompt = prompt
        this.reRender()
    }

    setGroupByOption(groupByFieldId:ID) {

        debug && console.log(debug, "setGroupByOption() groupByFieldId=", groupByFieldId)

        this.groupByFieldId     = groupByFieldId
        this.groupByValue       = AddUser_ALL

        this.reRender()
    }

    setGroupByValue(value:any) {

        debug && console.log(debug, "setGroupByValue() value=", value)

        this.groupByValue     = value
        this.reRender()
    }



    get modalTitle():string {

        switch (this.type) {
            case AddUserType.WORKSPACE_USER:
                return "Add Workspace User"

            case AddUserType.PROJECT_USER:
                return 'Add {0} User'.f(this.project?.systemTypeName || 'Project')

            case AddUserType.TASK_USER:
                return 'Add {0} User'.f(this.task?.title || 'Task')

            default:
                return "Add User"
        }

    }

    get groupByMenu():MenuItem[] {

        switch (this.groupByFieldId) {

            case 'departmentId': {

                const items : MenuItem[] = [
                    {
                        label       : 'All departments',
                        id          : AddUser_ALL,
                        onClick     : ()=>{
                            this.setGroupByValue(AddUser_ALL)
                        }
                    }
                ]

                gd.session.departments.all.map((department:Department) => {
                    items.push({
                        label       : department.name,
                        id          : department.id,
                        onClick     : ()=>{
                            this.setGroupByValue(department.id)
                        }
                    })
                })

                return items
            }


            case 'skills': {
                const items : MenuItem[] = [
                    {
                        label       : 'All users',
                        id          : AddUser_ALL,
                        onClick     : ()=>{
                            this.setGroupByValue(AddUser_ALL)
                        }
                    }
                ]

                const userSkillsCollection = UserSkillsCollection.initFromJson(gd.company.settings.modules.skills.options)

                userSkillsCollection.all.map((skill:UserSkill) => {
                    items.push({
                        label       : skill.label,
                        id          : skill.value,
                        onClick     : ()=>{
                            this.setGroupByValue(skill.value)
                        }
                    })
                })

                return items
            }


            case 'companyRole': {
                const items : MenuItem[] = [
                    {
                        label       : 'All users',
                        id          : AddUser_ALL,
                        onClick     : ()=>{
                            this.setGroupByValue(AddUser_ALL)
                        }
                    }
                ]

                items.push({
                    id          : CompanyRole.MANAGER,
                    label       : CompanyRoleLabel[CompanyRole.MANAGER],
                    onClick     : ()=>{
                        this.setGroupByValue(CompanyRole.MANAGER)
                    }
                })
                items.push({
                    id          : CompanyRole.USER,
                    label       : CompanyRoleLabel[CompanyRole.USER],
                    onClick     : ()=>{
                        this.setGroupByValue(CompanyRole.USER)
                    }
                })
                items.push({
                    id          : CompanyRole.GUEST,
                    label       : CompanyRoleLabel[CompanyRole.GUEST],
                    onClick     : ()=>{
                        this.setGroupByValue(CompanyRole.GUEST)
                    }
                })

                return items
            }


            default:
                // this is for custom fields ?
                return []



        }


    }

    get allUsersToAddSelected() {
        return _.compact([
            ...this.companyUsersToAdd.selected,
            ...this.workspaceUsersToAdd.selected,
            ...this.projectUsersToAdd.selected,
        ])
    }

    unselectAll() {
        this.companyUsersToAdd.unselectAll()
        this.workspaceUsersToAdd.unselectAll()
        this.projectUsersToAdd.unselectAll()
    }

    excludeUsers(excludeUsersIds?: ID[]) {
        _.map(excludeUsersIds, (id: ID)=>{
            this.companyUsersToAdd.remove(id)
            this.workspaceUsersToAdd.remove(id)
            this.projectUsersToAdd.remove(id)
        })
    }

    async createUsers() {
        await AddUser_createUsers(this)
    }

    public submit = async () => {

        // check if users creation requests are needed
        if (!this.noUsersCreation) {
            await this.createUsers()
        }

        gd.modal.hide()
        this.callback?.(this)
    }


}